import { Typography, Box, Grid, FormControlLabel, TextField as MuiTextField } from '@mui/material';
import { Form, useFormik, FormikProvider } from 'formik';
import { Button, Checkbox, Loader, TextField } from '../../../components';
import { useContext } from 'react';
import { UserContext } from '../../../context';
import { string as YupString, boolean as YupBoolean, object as YupObject } from 'yup';
import { submitWarrantyRequest } from '../../../fetch';
import { useSnackbar } from 'notistack';
import { formatInputPhoneNumber } from '../../../helpers';
import { useBuilds } from '../context';

export const WarrantyAskForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);
  const { selectedBuild } = useBuilds();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: user?.externalUser?.firstName ?? '',
      lastName: user?.externalUser?.lastName ?? '',
      email: '',
      phone: '',
      message: '',
      hasAgreement: false
    },
    validationSchema: () =>
      YupObject().shape({
        firstName: YupString().required('Required'),
        lastName: YupString().required('Required'),
        email: YupString().email('Invalid email address').required('Required'),
        phone: YupString().required('Required'),
        message: YupString().required('Required'),
        hasAgreement: YupBoolean().oneOf([true], 'Required')
      }),
    onSubmit: async (values, actions) => {
      try {
        // call api
        await submitWarrantyRequest(selectedBuild.buildId, values);
        enqueueSnackbar('Message submitted successfully', { variant: 'success' });
        actions.resetForm();
      } catch (error) {
        enqueueSnackbar(error?.Detail || 'An error occurred', { variant: 'error' });
      }
    }
  });
  return (
    <Box
      p={{
        xs: 4,
        sm: 8
      }}
      position="relative"
    >
      <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" mb={4}>
        <Typography component="h3" variant="h2">
          Need to Contact Someone Directly With a Question?
        </Typography>
      </Box>
      <FormikProvider value={formik}>
        {formik.isSubmitting && (
          <Loader type="overlay" position="centered">
            Submitting...
          </Loader>
        )}
        <Form onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                name="firstName"
                label="First Name"
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                name="lastName"
                label="Last Name"
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                name="email"
                label="Email"
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MuiTextField
                fullWidth
                required
                variant="outlined"
                autoComplete="nope"
                label="Phone"
                name="phone"
                value={formik.values.phone}
                onBlur={formik.handleBlur}
                size="small"
                onChange={e => {
                  formik.setFieldValue('phone', formatInputPhoneNumber(e.target.value));
                }}
                error={Boolean(formik.touched.phone && formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                name="message"
                label="How can we help you?"
                multiline
                rows={4}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiTypography-root': {
                    fontSize: '12px'
                  }
                }}
                control={<Checkbox name="hasAgreement" required sx={{ padding: `0 9px` }} />}
                label="By providing your name and contact information and clicking the submission button, you consent and agree to receive marketing communications from us or made on our behalf, including emails and calls or text messages using an automatic telephone dialing system or an artificial or prerecorded voice. Your consent and agreement to the foregoing, including the receipt of autodialed marketing messages, is not a condition of purchasing any property, goods or services from us, and you may call us instead of submitting your information online. Your consent herein also applies to any future registration on national or state Do Not Call lists. For mobile phones, standard text message and data charges apply."
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                id="warranty-submit-button"
                variant="contained"
                sx={{
                  width: { xs: '100%', md: '275px' },
                  mt: 1,
                  mb: 1
                }}
                disabled={formik.isSubmitting || !formik.isValid}
                type="submit"
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </Box>
  );
};
